import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useMinistry } from '@/contexts/MinistryContext';
import { toast } from 'sonner';
import { BarChart3, TrendingUp, Users, Calendar, Cake, Download, FileSpreadsheet, User, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const Reports = () => {
  const { currentMinistry } = useMinistry();
  
  // Estado para Relatório de Presença
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  // Estado para Relatório de Aniversariantes
  const [birthStartDate, setBirthStartDate] = useState('');
  const [birthEndDate, setBirthEndDate] = useState('');
  const [birthdayReport, setBirthdayReport] = useState(null);
  const [birthdayLoading, setBirthdayLoading] = useState(false);
  const [birthdayGenderFilter, setBirthdayGenderFilter] = useState('all');
  const [birthdaySearchFilter, setBirthdaySearchFilter] = useState('');
  const [birthdayAgeMin, setBirthdayAgeMin] = useState('');
  const [birthdayAgeMax, setBirthdayAgeMax] = useState('');
  const [adolescents, setAdolescents] = useState([]);

  // Carregar adolescentes ao montar
  useEffect(() => {
    if (currentMinistry?.id) {
      fetchAdolescents();
    }
  }, [currentMinistry?.id]);

  const fetchAdolescents = async () => {
    try {
      const response = await axios.get(`${API}/adolescents?ministry_id=${currentMinistry.id}`);
      setAdolescents(response.data);
    } catch (error) {
      console.error('Erro ao carregar adolescentes:', error);
    }
  };

  // Relatório de Presença
  const fetchReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Selecione um período');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/reports/attendance?start_date=${startDate}&end_date=${endDate}`
      );
      setReport(response.data);
    } catch (error) {
      toast.error('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!report || !report.events) return;
    
    let csv = 'Evento,Total,Membros,Visitantes,Visitantes de Primeira Vez\n';
    report.events.forEach(event => {
      csv += `${event.event_name},${event.total},${event.members},${event.visitors},${event.first_time}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio-presenca-${startDate}-${endDate}.csv`;
    a.click();
    toast.success('Relatório exportado com sucesso!');
  };

  // Relatório de Aniversariantes
  const generateBirthdayReport = () => {
    if (!birthStartDate || !birthEndDate) {
      toast.error('Selecione um período de datas');
      return;
    }

    setBirthdayLoading(true);
    
    try {
      // Parsear as datas de início e fim
      const startParts = birthStartDate.split('-');
      const endParts = birthEndDate.split('-');
      
      const startMonth = parseInt(startParts[1]);
      const startDay = parseInt(startParts[2]);
      const endMonth = parseInt(endParts[1]);
      const endDay = parseInt(endParts[2]);

      // Parsear filtros de idade
      const minAge = birthdayAgeMin ? parseInt(birthdayAgeMin) : null;
      const maxAge = birthdayAgeMax ? parseInt(birthdayAgeMax) : null;

      // Filtrar adolescentes que nasceram no período
      const filtered = adolescents.filter(a => {
        if (!a.birth_date) return false;
        
        // Filtro de faixa etária
        if (minAge !== null && a.age < minAge) return false;
        if (maxAge !== null && a.age > maxAge) return false;
        
        const birthParts = a.birth_date.split('-');
        const birthMonth = parseInt(birthParts[1]);
        const birthDay = parseInt(birthParts[2]);
        
        // Verificar se a data de nascimento está no período
        // Considerar apenas mês e dia (ignorar ano)
        const birthDateValue = birthMonth * 100 + birthDay;
        const startValue = startMonth * 100 + startDay;
        const endValue = endMonth * 100 + endDay;
        
        // Se o período não cruza o ano (ex: março a maio)
        if (startValue <= endValue) {
          return birthDateValue >= startValue && birthDateValue <= endValue;
        } else {
          // Se o período cruza o ano (ex: novembro a janeiro)
          return birthDateValue >= startValue || birthDateValue <= endValue;
        }
      });

      // Ordenar por mês/dia de nascimento
      const sorted = filtered.sort((a, b) => {
        const aDate = a.birth_date.split('-');
        const bDate = b.birth_date.split('-');
        const aValue = parseInt(aDate[1]) * 100 + parseInt(aDate[2]);
        const bValue = parseInt(bDate[1]) * 100 + parseInt(bDate[2]);
        return aValue - bValue;
      });

      setBirthdayReport({
        total: sorted.length,
        adolescents: sorted,
        period: {
          start: birthStartDate,
          end: birthEndDate
        }
      });

      toast.success(`${sorted.length} aniversariante(s) encontrado(s)`);
    } catch (error) {
      toast.error('Erro ao gerar relatório de aniversariantes');
    } finally {
      setBirthdayLoading(false);
    }
  };

  // Filtrar resultados do relatório de aniversariantes
  const getFilteredBirthdayResults = () => {
    if (!birthdayReport?.adolescents) return [];
    
    return birthdayReport.adolescents.filter(a => {
      if (birthdayGenderFilter !== 'all' && a.gender !== birthdayGenderFilter) return false;
      if (birthdaySearchFilter && !a.name.toLowerCase().includes(birthdaySearchFilter.toLowerCase())) return false;
      return true;
    });
  };

  // Exportar relatório de aniversariantes
  const exportBirthdayToCSV = () => {
    const data = getFilteredBirthdayResults();
    if (data.length === 0) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const headers = ['Nome', 'Data Nascimento', 'Idade', 'Gênero', 'Telefone', 'Responsável', 'Tel. Responsável'];
    const rows = data.map(a => [
      a.name || '',
      a.birth_date ? new Date(a.birth_date + 'T12:00:00').toLocaleDateString('pt-BR') : '',
      a.age || '',
      a.gender === 'masculino' ? 'Masculino' : 'Feminino',
      a.phone || '',
      a.guardian_name || '',
      a.guardian_phone || ''
    ]);

    const BOM = '\uFEFF';
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `aniversariantes_${birthStartDate}_${birthEndDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`${data.length} aniversariante(s) exportado(s)`);
  };

  const exportBirthdayToExcel = () => {
    const data = getFilteredBirthdayResults();
    if (data.length === 0) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    let tableHTML = `
      <table>
        <thead>
          <tr>
            <th>Nome</th>
            <th>Data Nascimento</th>
            <th>Idade</th>
            <th>Gênero</th>
            <th>Telefone</th>
            <th>Responsável</th>
            <th>Tel. Responsável</th>
          </tr>
        </thead>
        <tbody>
    `;

    data.forEach(a => {
      const birthDateFormatted = a.birth_date ? new Date(a.birth_date + 'T12:00:00').toLocaleDateString('pt-BR') : '';
      tableHTML += `
        <tr>
          <td>${a.name || ''}</td>
          <td>${birthDateFormatted}</td>
          <td>${a.age || ''}</td>
          <td>${a.gender === 'masculino' ? 'Masculino' : 'Feminino'}</td>
          <td>${a.phone || ''}</td>
          <td>${a.guardian_name || ''}</td>
          <td>${a.guardian_phone || ''}</td>
        </tr>
      `;
    });

    tableHTML += '</tbody></table>';

    const blob = new Blob([tableHTML], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `aniversariantes_${birthStartDate}_${birthEndDate}.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`${data.length} aniversariante(s) exportado(s)`);
  };

  // Funções auxiliares para datas rápidas
  const setCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
    setBirthStartDate(`${year}-${month}-01`);
    setBirthEndDate(`${year}-${month}-${lastDay}`);
  };

  const setNextMonth = () => {
    const now = new Date();
    now.setMonth(now.getMonth() + 1);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
    setBirthStartDate(`${year}-${month}-01`);
    setBirthEndDate(`${year}-${month}-${lastDay}`);
  };

  const filteredBirthdayResults = getFilteredBirthdayResults();

  return (
    <div className="fade-in" data-testid="reports-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Space Grotesk'}}>Relatórios</h1>
        <p className="text-lg text-gray-600">Análise de presença, crescimento e aniversariantes</p>
      </div>

      <Tabs defaultValue="attendance" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
          <TabsTrigger value="attendance" className="flex items-center gap-2">
            <Calendar size={16} />
            Presença
          </TabsTrigger>
          <TabsTrigger value="birthdays" className="flex items-center gap-2">
            <Cake size={16} />
            Aniversariantes
          </TabsTrigger>
        </TabsList>

        {/* Aba de Relatório de Presença */}
        <TabsContent value="attendance">
          {/* Filters */}
          <div className="card mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Space Grotesk'}}>Período do Relatório</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Data Inicial</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="start-date-input"
                />
              </div>
              <div>
                <Label>Data Final</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="end-date-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={fetchReport}
                  className="btn-primary w-full"
                  disabled={loading}
                  data-testid="generate-report-button"
                >
                  {loading ? 'Gerando...' : 'Gerar Relatório'}
                </Button>
              </div>
            </div>
          </div>

          {/* Report Results */}
          {report && (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="stat-card">
                  <div className="stat-icon">
                    <BarChart3 size={28} />
                  </div>
                  <div className="stat-value">{report.total_checkins}</div>
                  <div className="stat-label">Total de Check-ins</div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', color: '#065f46'}}>
                    <Calendar size={28} />
                  </div>
                  <div className="stat-value">{report.events?.length || 0}</div>
                  <div className="stat-label">Eventos Realizados</div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', color: '#92400e'}}>
                    <TrendingUp size={28} />
                  </div>
                  <div className="stat-value">
                    {report.events?.length > 0 ? Math.round(report.total_checkins / report.events.length) : 0}
                  </div>
                  <div className="stat-label">Média por Evento</div>
                </div>
              </div>

              {/* Events Table */}
              <div className="card mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>Detalhes por Evento</h2>
                  <Button onClick={exportToCSV} variant="outline" data-testid="export-csv-button">
                    <Download size={16} className="mr-2" />
                    Exportar CSV
                  </Button>
                </div>
                
                {report.events && report.events.length > 0 ? (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Evento</th>
                          <th>Total</th>
                          <th>Membros</th>
                          <th>Visitantes</th>
                          <th>Visitantes 1ª Vez</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.events.map((event, index) => (
                          <tr key={index} data-testid="event-report-row">
                            <td className="font-semibold">{event.event_name}</td>
                            <td>{event.total}</td>
                            <td>{event.members}</td>
                            <td>{event.visitors}</td>
                            <td>{event.first_time}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center py-8 text-gray-500">Nenhum evento encontrado no período</p>
                )}
              </div>
            </>
          )}

          {!report && (
            <div className="card text-center py-12">
              <BarChart3 size={64} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 text-lg">Selecione um período e gere um relatório</p>
            </div>
          )}
        </TabsContent>

        {/* Aba de Relatório de Aniversariantes */}
        <TabsContent value="birthdays">
          <div className="card mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Space Grotesk'}}>
              <Cake className="inline mr-2 text-pink-500" size={24} />
              Filtrar por Data de Nascimento e Idade
            </h2>
            <p className="text-gray-600 mb-4">Encontre adolescentes que fazem aniversário em um determinado período, com filtro opcional de faixa etária.</p>
            
            {/* Botões de atalho */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Button variant="outline" size="sm" onClick={setCurrentMonth}>
                Este mês
              </Button>
              <Button variant="outline" size="sm" onClick={setNextMonth}>
                Próximo mês
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <Label>Data Início do Período</Label>
                <Input
                  type="date"
                  value={birthStartDate}
                  onChange={(e) => setBirthStartDate(e.target.value)}
                  data-testid="birth-start-date-input"
                />
                <p className="text-xs text-gray-500 mt-1">O ano será ignorado (apenas mês/dia)</p>
              </div>
              <div>
                <Label>Data Fim do Período</Label>
                <Input
                  type="date"
                  value={birthEndDate}
                  onChange={(e) => setBirthEndDate(e.target.value)}
                  data-testid="birth-end-date-input"
                />
              </div>
              <div>
                <Label>Idade Mínima</Label>
                <Input
                  type="number"
                  min="0"
                  max="99"
                  placeholder="Ex: 12"
                  value={birthdayAgeMin}
                  onChange={(e) => setBirthdayAgeMin(e.target.value)}
                  data-testid="birth-age-min-input"
                />
                <p className="text-xs text-gray-500 mt-1">Opcional</p>
              </div>
              <div>
                <Label>Idade Máxima</Label>
                <Input
                  type="number"
                  min="0"
                  max="99"
                  placeholder="Ex: 15"
                  value={birthdayAgeMax}
                  onChange={(e) => setBirthdayAgeMax(e.target.value)}
                  data-testid="birth-age-max-input"
                />
                <p className="text-xs text-gray-500 mt-1">Opcional</p>
              </div>
            </div>

            {/* Atalhos de faixa etária */}
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="text-sm text-gray-500 flex items-center mr-2">Faixas rápidas:</span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { setBirthdayAgeMin('10'); setBirthdayAgeMax('12'); }}
                className={birthdayAgeMin === '10' && birthdayAgeMax === '12' ? 'border-blue-500 bg-blue-50' : ''}
              >
                10-12 anos
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { setBirthdayAgeMin('13'); setBirthdayAgeMax('14'); }}
                className={birthdayAgeMin === '13' && birthdayAgeMax === '14' ? 'border-blue-500 bg-blue-50' : ''}
              >
                13-14 anos
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { setBirthdayAgeMin('15'); setBirthdayAgeMax('17'); }}
                className={birthdayAgeMin === '15' && birthdayAgeMax === '17' ? 'border-blue-500 bg-blue-50' : ''}
              >
                15-17 anos
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { setBirthdayAgeMin(''); setBirthdayAgeMax(''); }}
                className={!birthdayAgeMin && !birthdayAgeMax ? 'border-green-500 bg-green-50' : ''}
              >
                Todas as idades
              </Button>
            </div>

            <div className="flex items-end">
              <Button
                onClick={generateBirthdayReport}
                className="btn-primary"
                disabled={birthdayLoading}
                data-testid="generate-birthday-report-button"
              >
                {birthdayLoading ? 'Buscando...' : 'Buscar Aniversariantes'}
              </Button>
              
              {(birthdayAgeMin || birthdayAgeMax) && (
                <span className="ml-4 text-sm text-blue-600">
                  Filtrando: {birthdayAgeMin || '0'} - {birthdayAgeMax || '99'} anos
                </span>
              )}
            </div>
          </div>

          {/* Resultados do Relatório de Aniversariantes */}
          {birthdayReport && (
            <>
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="stat-card">
                  <div className="stat-icon" style={{background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)', color: '#be185d'}}>
                    <Cake size={28} />
                  </div>
                  <div className="stat-value">{birthdayReport.total}</div>
                  <div className="stat-label">Total de Aniversariantes</div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', color: '#1e40af'}}>
                    <User size={28} />
                  </div>
                  <div className="stat-value">
                    {birthdayReport.adolescents.filter(a => a.gender === 'masculino').length}
                  </div>
                  <div className="stat-label">👦 Meninos</div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)', color: '#be185d'}}>
                    <User size={28} />
                  </div>
                  <div className="stat-value">
                    {birthdayReport.adolescents.filter(a => a.gender === 'feminino').length}
                  </div>
                  <div className="stat-label">👧 Meninas</div>
                </div>
              </div>

              {/* Filtros e Tabela */}
              <div className="card">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4">
                  <h2 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                    Lista de Aniversariantes
                  </h2>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={exportBirthdayToCSV}>
                      <FileSpreadsheet size={16} className="mr-1 text-green-600" />
                      CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportBirthdayToExcel}>
                      <FileSpreadsheet size={16} className="mr-1 text-blue-600" />
                      Excel
                    </Button>
                  </div>
                </div>

                {/* Filtros da tabela */}
                <div className="flex flex-wrap gap-4 mb-4 pb-4 border-b">
                  <div className="flex items-center gap-2">
                    <Search size={18} className="text-gray-400" />
                    <Input
                      placeholder="Buscar por nome..."
                      value={birthdaySearchFilter}
                      onChange={(e) => setBirthdaySearchFilter(e.target.value)}
                      className="w-48"
                    />
                  </div>
                  
                  <Select value={birthdayGenderFilter} onValueChange={setBirthdayGenderFilter}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Gênero" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="masculino">👦 Meninos</SelectItem>
                      <SelectItem value="feminino">👧 Meninas</SelectItem>
                    </SelectContent>
                  </Select>

                  {(birthdaySearchFilter || birthdayGenderFilter !== 'all') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setBirthdaySearchFilter('');
                        setBirthdayGenderFilter('all');
                      }}
                    >
                      Limpar filtros
                    </Button>
                  )}

                  <span className="text-sm text-gray-500 ml-auto">
                    Mostrando {filteredBirthdayResults.length} de {birthdayReport.total}
                  </span>
                </div>

                {filteredBirthdayResults.length > 0 ? (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Nome</th>
                          <th>Data Nascimento</th>
                          <th>Idade</th>
                          <th>Gênero</th>
                          <th>Telefone</th>
                          <th>Responsável</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBirthdayResults.map((adolescent) => (
                          <tr key={adolescent.id} data-testid="birthday-report-row">
                            <td>
                              <div className="flex items-center gap-2">
                                {adolescent.photo_url ? (
                                  <img src={adolescent.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                                    <User size={16} className="text-gray-500" />
                                  </div>
                                )}
                                <span className="font-semibold">{adolescent.name}</span>
                              </div>
                            </td>
                            <td>
                              <div className="flex items-center gap-2">
                                <Cake size={14} className="text-pink-500" />
                                {adolescent.birth_date ? new Date(adolescent.birth_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}
                              </div>
                            </td>
                            <td>{adolescent.age} anos</td>
                            <td>
                              <span className={adolescent.gender === 'masculino' ? 'text-blue-600' : 'text-pink-600'}>
                                {adolescent.gender === 'masculino' ? '👦' : '👧'}
                              </span>
                            </td>
                            <td>{adolescent.phone || '-'}</td>
                            <td>
                              <div>
                                <p className="text-sm">{adolescent.guardian_name || '-'}</p>
                                {adolescent.guardian_phone && (
                                  <p className="text-xs text-gray-500">{adolescent.guardian_phone}</p>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center py-8 text-gray-500">
                    {birthdayReport.total === 0 
                      ? 'Nenhum aniversariante encontrado no período selecionado'
                      : 'Nenhum resultado com os filtros aplicados'}
                  </p>
                )}
              </div>
            </>
          )}

          {!birthdayReport && (
            <div className="card text-center py-12">
              <Cake size={64} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 text-lg">Selecione um período para encontrar aniversariantes</p>
              <p className="text-gray-400 text-sm mt-2">O sistema irá buscar todos os adolescentes que fazem aniversário entre as datas selecionadas</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
